import { apiRequest } from '../../../shared/services/apiClient';
import {
  normalizeKnowledgeConsultationMetadataFields,
} from '../utils/knowledgeConsultationMetadata';
import {
  KnowledgeDocument,
  KnowledgeBaseHealth,
  KnowledgeRetrievalMetrics,
  KnowledgeBaseSettings,
  KnowledgeBaseTenantSettingsResponse,
  KnowledgeDocumentListQuery,
  KnowledgeIngestionJob,
  KnowledgeRetrievalLog,
  KnowledgeRetrievalLogListQuery,
  KnowledgeRetrieveRequest,
  KnowledgeRetrieveResponse,
  KnowledgeVisibility,
  PagedKnowledgeDocumentResult,
  PagedKnowledgeRetrievalLogResult,
  ChangeActivityItem,
  ChangeActivityListQuery,
  PagedChangeActivityResult,
  ReprocessKnowledgeDocumentRequest,
  SubmitKnowledgeRetrievalFeedbackRequest,
  UpdateKnowledgeBaseSettingsRequest,
  UpdateKnowledgeDocumentSummaryRequest,
  UpdateKnowledgeDocumentTagsRequest,
  UpdateKnowledgeDocumentTitleRequest,
  UploadKnowledgeDocumentRequest,
  UploadKnowledgeAttachmentRequest,
  UpdateKnowledgeAttachmentRequest,
  KnowledgeAttachment,
  KnowledgeAttachmentSettings,
  KnowledgeDataCategory,
  KnowledgeDataCategoryOverview,
  KnowledgeDataSourceType,
  KnowledgeExternalSyncAuthType,
  KnowledgeConsultationSource,
  KnowledgeGoogleSheetsSource,
  CreateKnowledgeDataCategoryRequest,
  UpdateKnowledgeDataCategoryRequest,
  GoogleSheetsOAuthStartResponse,
  KnowledgeGoogleSheetsSettingsResponse,
  UpdateKnowledgeGoogleSheetsSettingsRequest,
  KnowledgeCategoryJsonSyncResult,
  KnowledgeDynamicTool,
  CreateKnowledgeDynamicToolRequest,
  UpdateKnowledgeDynamicToolRequest,
  JsonSchemaIntrospectionResult,
  IntrospectKnowledgeJsonSchemaRequest,
  PreviewKnowledgeDynamicToolRequest,
  PreviewKnowledgeDynamicToolResult,
} from '../types/Knowledge';

const normalizeKnowledgeBaseSettings = (value: any): KnowledgeBaseSettings => ({
  retrieval: {
    semanticScoreThreshold: Number(value?.retrieval?.semanticScoreThreshold ?? 0),
    defaultMaxChunks: Number(value?.retrieval?.defaultMaxChunks ?? 5),
    maxAllowedChunks: Number(value?.retrieval?.maxAllowedChunks ?? 20),
    queryMessageCount: Number(value?.retrieval?.queryMessageCount ?? 3),
    embeddingQueryMaxChars: Number(value?.retrieval?.embeddingQueryMaxChars ?? 1000),
    keywordQueryMaxChars: Number(value?.retrieval?.keywordQueryMaxChars ?? 180),
  },
  ingestion: {
    chunkMaxChars: Number(value?.ingestion?.chunkMaxChars ?? 1200),
    chunkOverlapChars: Number(value?.ingestion?.chunkOverlapChars ?? 150),
    pollIntervalSeconds: Number(value?.ingestion?.pollIntervalSeconds ?? 2),
    retryMaxAttempts: Number(value?.ingestion?.retryMaxAttempts ?? 3),
    retryBackoffBaseSeconds: Number(value?.ingestion?.retryBackoffBaseSeconds ?? 15),
    retryBackoffMaxSeconds: Number(value?.ingestion?.retryBackoffMaxSeconds ?? 300),
    stuckQueueAlertThresholdSeconds: Number(value?.ingestion?.stuckQueueAlertThresholdSeconds ?? 300),
    autoRepairEnabled: Boolean(value?.ingestion?.autoRepairEnabled),
    autoRepairIntervalSeconds: Number(value?.ingestion?.autoRepairIntervalSeconds ?? 60),
    autoRepairMaxDocuments: Number(value?.ingestion?.autoRepairMaxDocuments ?? 100),
    autoRepairIncludeFailedDocuments: value?.ingestion?.autoRepairIncludeFailedDocuments !== false,
    autoRepairIncludeUploadedDocuments: value?.ingestion?.autoRepairIncludeUploadedDocuments !== false,
    autoRepairMinDocumentAgeMinutes: Number(value?.ingestion?.autoRepairMinDocumentAgeMinutes ?? 5),
  },
  attachments: {
    enabled: value?.attachments?.enabled !== false,
    maxAttachmentsPerDocument: Number(value?.attachments?.maxAttachmentsPerDocument ?? 20),
    maxAttachmentBytes: Number(value?.attachments?.maxAttachmentBytes ?? 10 * 1024 * 1024),
    maxVideoAttachmentBytes: Number(value?.attachments?.maxVideoAttachmentBytes ?? 50 * 1024 * 1024),
    maxCaptionLength: Number(value?.attachments?.maxCaptionLength ?? 500),
    maxAltTextLength: Number(value?.attachments?.maxAltTextLength ?? 500),
    allowedImageMimeTypesCsv: String(value?.attachments?.allowedImageMimeTypesCsv ?? 'image/jpeg,image/png,image/webp'),
    allowedVideoMimeTypesCsv: String(value?.attachments?.allowedVideoMimeTypesCsv ?? 'video/mp4,video/webm,video/quicktime'),
  },
});

const normalizeKnowledgeBaseTenantSettingsResponse = (value: any): KnowledgeBaseTenantSettingsResponse => ({
  effective: normalizeKnowledgeBaseSettings(value?.effective ?? value),
  overrides: value?.overrides && typeof value.overrides === 'object'
    ? Object.fromEntries(Object.entries(value.overrides).map(([key, entryValue]) => [String(key), String(entryValue)]))
    : {},
});

const normalizeKnowledgeBaseHealth = (value: any): KnowledgeBaseHealth => ({
  generatedAt: String(value?.generatedAt ?? ''),
  documentsTotal: Number(value?.documentsTotal ?? 0),
  documentsReady: Number(value?.documentsReady ?? 0),
  documentsFailed: Number(value?.documentsFailed ?? 0),
  chunksTotal: Number(value?.chunksTotal ?? 0),
  embeddedChunks: Number(value?.embeddedChunks ?? 0),
  jobsQueued: Number(value?.jobsQueued ?? 0),
  jobsProcessing: Number(value?.jobsProcessing ?? 0),
  jobsFailed: Number(value?.jobsFailed ?? 0),
  jobsDeadLetter: Number(value?.jobsDeadLetter ?? 0),
  oldestQueuedJobCreatedAt: typeof value?.oldestQueuedJobCreatedAt === 'string' ? value.oldestQueuedJobCreatedAt : null,
  oldestQueuedJobAgeSeconds: Number.isFinite(Number(value?.oldestQueuedJobAgeSeconds)) ? Number(value.oldestQueuedJobAgeSeconds) : null,
  isIngestionStuck: Boolean(value?.isIngestionStuck),
  stuckQueueAlertThresholdSeconds: Number(value?.stuckQueueAlertThresholdSeconds ?? 0),
  semanticScoreThreshold: Number(value?.semanticScoreThreshold ?? 0),
  chunkMaxChars: Number(value?.chunkMaxChars ?? 0),
  chunkOverlapChars: Number(value?.chunkOverlapChars ?? 0),
  retryMaxAttempts: Number(value?.retryMaxAttempts ?? 0),
  retryBackoffBaseSeconds: Number(value?.retryBackoffBaseSeconds ?? 0),
  retryBackoffMaxSeconds: Number(value?.retryBackoffMaxSeconds ?? 0),
});

const normalizeKnowledgeRetrievalMetrics = (value: any): KnowledgeRetrievalMetrics => ({
  windowStart: String(value?.windowStart ?? ''),
  windowEnd: String(value?.windowEnd ?? ''),
  totalRequests: Number(value?.totalRequests ?? 0),
  sampledRequests: Number(value?.sampledRequests ?? 0),
  emptyResultCount: Number(value?.emptyResultCount ?? 0),
  emptyResultRate: Number(value?.emptyResultRate ?? 0),
  p50LatencyMilliseconds: Number(value?.p50LatencyMilliseconds ?? 0),
  p95LatencyMilliseconds: Number(value?.p95LatencyMilliseconds ?? 0),
  semanticRequests: Number(value?.semanticRequests ?? 0),
  keywordRequests: Number(value?.keywordRequests ?? 0),
  hybridRequests: Number(value?.hybridRequests ?? 0),
});

const normalizeKnowledgeDocument = (value: any): KnowledgeDocument => ({
  id: String(value?.id ?? value?.Id ?? ''),
  title: String(value?.title ?? ''),
  originalFileName: String(value?.originalFileName ?? ''),
  storedFileName: String(value?.storedFileName ?? ''),
  mimeType: String(value?.mimeType ?? ''),
  fileSize: Number(value?.fileSize ?? 0),
  mediaUrl: String(value?.mediaUrl ?? ''),
  publicUrl: String(value?.publicUrl ?? value?.PublicUrl ?? value?.mediaUrl ?? ''),
  visibility: value?.visibility === 'customer_safe' ? 'customer_safe' : value?.visibility === 'public' ? 'public' : 'internal',
  processingStatus: typeof value?.processingStatus === 'string' ? value.processingStatus : 'uploaded',
  tags: Array.isArray(value?.tags) ? value.tags.map(String) : [],
  createdByEmployeeId: String(value?.createdByEmployeeId ?? ''),
  createdAt: String(value?.createdAt ?? ''),
  updatedAt: String(value?.updatedAt ?? ''),
  summary: typeof value?.summary === 'string' ? value.summary : null,
  fileCategory: typeof value?.fileCategory === 'string'
    ? value.fileCategory
    : typeof value?.file_category === 'string'
      ? value.file_category
      : null,
  attachmentCount: Number(value?.attachmentCount ?? 0),
});

const normalizeKnowledgeAttachment = (value: any): KnowledgeAttachment => ({
  id: String(value?.id ?? ''),
  knowledgeDocumentId: String(value?.knowledgeDocumentId ?? ''),
  role: String(value?.role ?? 'supplementary'),
  sortOrder: Number(value?.sortOrder ?? 0),
  fileName: String(value?.fileName ?? ''),
  mimeType: String(value?.mimeType ?? ''),
  fileSize: Number(value?.fileSize ?? 0),
  mediaUrl: String(value?.mediaUrl ?? ''),
  accessibleUrl: String(value?.accessibleUrl ?? value?.mediaUrl ?? ''),
  caption: String(value?.caption ?? ''),
  altText: String(value?.altText ?? ''),
  createdByEmployeeId: String(value?.createdByEmployeeId ?? ''),
  createdAt: String(value?.createdAt ?? ''),
  updatedAt: String(value?.updatedAt ?? ''),
});

const normalizeKnowledgeAttachmentSettings = (value: any): KnowledgeAttachmentSettings => ({
  enabled: value?.enabled !== false,
  maxAttachmentsPerDocument: Number(value?.maxAttachmentsPerDocument ?? 20),
  maxAttachmentBytes: Number(value?.maxAttachmentBytes ?? 10 * 1024 * 1024),
  maxVideoAttachmentBytes: Number(value?.maxVideoAttachmentBytes ?? 50 * 1024 * 1024),
  maxCaptionLength: Number(value?.maxCaptionLength ?? 500),
  maxAltTextLength: Number(value?.maxAltTextLength ?? 500),
  allowedImageMimeTypesCsv: String(value?.allowedImageMimeTypesCsv ?? 'image/jpeg,image/png,image/webp'),
  allowedVideoMimeTypesCsv: String(value?.allowedVideoMimeTypesCsv ?? 'video/mp4,video/webm,video/quicktime'),
});

const normalizePagedResult = (value: any): PagedKnowledgeDocumentResult => ({
  page: Number(value?.page ?? 1),
  pageSize: Number(value?.pageSize ?? 25),
  totalCount: Number(value?.totalCount ?? 0),
  items: Array.isArray(value?.items) ? value.items.map(normalizeKnowledgeDocument) : [],
});

const normalizeIngestionJob = (value: any): KnowledgeIngestionJob => ({
  id: String(value?.id ?? ''),
  knowledgeDocumentId: String(value?.knowledgeDocumentId ?? ''),
  status: String(value?.status ?? ''),
  attempts: Number(value?.attempts ?? 0),
  error: typeof value?.error === 'string' ? value.error : null,
  createdAt: String(value?.createdAt ?? ''),
  updatedAt: String(value?.updatedAt ?? ''),
  startedAt: typeof value?.startedAt === 'string' ? value.startedAt : null,
  completedAt: typeof value?.completedAt === 'string' ? value.completedAt : null,
});

const normalizeRetrieveResponse = (value: any): KnowledgeRetrieveResponse => ({
  query: String(value?.query ?? ''),
  chunks: Array.isArray(value?.chunks)
    ? value.chunks.map((chunk: any) => ({
      chunkId: String(chunk?.chunkId ?? ''),
      documentId: String(chunk?.documentId ?? ''),
      documentName: String(chunk?.documentName ?? ''),
      text: String(chunk?.text ?? ''),
      visibility: String(chunk?.visibility ?? ''),
      chunkIndex: Number(chunk?.chunkIndex ?? 0),
    }))
    : [],
});

const normalizeKnowledgeRetrievalLog = (value: any): KnowledgeRetrievalLog => ({
  id: String(value?.id ?? ''),
  conversationId: typeof value?.conversationId === 'string' ? value.conversationId : null,
  contextType: typeof value?.contextType === 'string' ? value.contextType : null,
  query: typeof value?.query === 'string' ? value.query : null,
  retrievalMethod: typeof value?.retrievalMethod === 'string' ? value.retrievalMethod : null,
  retrievedChunkIds: typeof value?.retrievedChunkIds === 'string' ? value.retrievedChunkIds : null,
  retrievedChunkCount: Number(value?.retrievedChunkCount ?? 0),
  averageRelevanceScore: typeof value?.averageRelevanceScore === 'number' ? value.averageRelevanceScore : null,
  latencyMilliseconds: Number(value?.latencyMilliseconds ?? 0),
  embeddingModel: typeof value?.embeddingModel === 'string' ? value.embeddingModel : null,
  userFoundHelpful: typeof value?.userFoundHelpful === 'boolean' ? value.userFoundHelpful : null,
  feedback: typeof value?.feedback === 'string' ? value.feedback : null,
  createdAt: String(value?.createdAt ?? ''),
});

const normalizePagedRetrievalLogResult = (value: any): PagedKnowledgeRetrievalLogResult => ({
  page: Number(value?.page ?? 1),
  pageSize: Number(value?.pageSize ?? 25),
  totalCount: Number(value?.totalCount ?? 0),
  items: Array.isArray(value?.items) ? value.items.map(normalizeKnowledgeRetrievalLog) : [],
});

export const listKnowledgeDocuments = async (
  query: KnowledgeDocumentListQuery = {},
): Promise<PagedKnowledgeDocumentResult> => {
  const params = new URLSearchParams();

  params.set('page', String(query.page && query.page > 0 ? query.page : 1));
  params.set('pageSize', String(query.pageSize && query.pageSize > 0 ? query.pageSize : 25));

  if (query.visibility && query.visibility !== 'all') {
    params.set('visibility', query.visibility);
  }

  if (query.status && query.status !== 'all') {
    params.set('status', query.status);
  }

  if (query.search?.trim()) {
    params.set('search', query.search.trim());
  }

  if (query.fileCategory && query.fileCategory !== 'all') {
    params.set('fileCategory', query.fileCategory);
  }

  const payload = await apiRequest<any>(`/api/v1/knowledge/documents?${params.toString()}`);
  return normalizePagedResult(payload);
};

export const uploadKnowledgeDocument = async (
  request: UploadKnowledgeDocumentRequest,
): Promise<KnowledgeDocument> => {
  const formData = new FormData();
  formData.append('file', request.file);
  formData.append('visibility', request.visibility ?? 'internal');

  if (request.title?.trim()) {
    formData.append('title', request.title.trim());
  }

  if (request.tags?.trim()) {
    formData.append('tags', request.tags.trim());
  }

  if (request.summary?.trim()) {
    formData.append('summary', request.summary.trim());
  }

  if (request.fileCategory?.trim()) {
    formData.append('fileCategory', request.fileCategory.trim());
  }

  const payload = await apiRequest<any>('/api/v1/knowledge/documents', {
    method: 'POST',
    body: formData,
  });

  return normalizeKnowledgeDocument(payload);
};

export const replaceKnowledgeDocumentFile = async (
  documentId: string,
  file: File,
): Promise<KnowledgeDocument> => {
  const formData = new FormData();
  formData.append('file', file);

  const payload = await apiRequest<any>(`/api/v1/knowledge/documents/${documentId}/file`, {
    method: 'POST',
    body: formData,
  });

  return normalizeKnowledgeDocument(payload);
};

export const updateKnowledgeDocumentSummary = async (
  documentId: string,
  request: UpdateKnowledgeDocumentSummaryRequest,
): Promise<KnowledgeDocument> => {
  const payload = await apiRequest<any>(`/api/v1/knowledge/documents/${documentId}/summary`, {
    method: 'PATCH',
    body: JSON.stringify({ summary: request.summary }),
  });

  return normalizeKnowledgeDocument(payload);
};

export const updateKnowledgeDocumentTitle = async (
  documentId: string,
  request: UpdateKnowledgeDocumentTitleRequest,
): Promise<KnowledgeDocument> => {
  const payload = await apiRequest<any>(`/api/v1/knowledge/documents/${documentId}/title`, {
    method: 'PATCH',
    body: JSON.stringify({ title: request.title }),
  });

  return normalizeKnowledgeDocument(payload);
};

export const updateKnowledgeDocumentTags = async (
  documentId: string,
  request: UpdateKnowledgeDocumentTagsRequest,
): Promise<KnowledgeDocument> => {
  const payload = await apiRequest<any>(`/api/v1/knowledge/documents/${documentId}/tags`, {
    method: 'PATCH',
    body: JSON.stringify({ tags: request.tags }),
  });

  return normalizeKnowledgeDocument(payload);
};

export const deleteKnowledgeDocument = async (documentId: string): Promise<void> => {
  await apiRequest<void>(`/api/v1/knowledge/documents/${documentId}`, {
    method: 'DELETE',
  });
};

export const listKnowledgeDocumentAttachments = async (documentId: string): Promise<KnowledgeAttachment[]> => {
  const payload = await apiRequest<unknown>(`/api/v1/knowledge/documents/${documentId}/attachments`);
  return Array.isArray(payload) ? payload.map(normalizeKnowledgeAttachment) : [];
};

export const getKnowledgeAttachmentSettings = async (): Promise<KnowledgeAttachmentSettings> => {
  const payload = await apiRequest<unknown>('/api/v1/knowledge/attachments/settings');
  return normalizeKnowledgeAttachmentSettings(payload);
};

export const uploadKnowledgeDocumentAttachment = async (
  documentId: string,
  request: UploadKnowledgeAttachmentRequest,
): Promise<KnowledgeAttachment> => {
  const formData = new FormData();
  formData.append('file', request.file);

  if (request.caption?.trim()) {
    formData.append('caption', request.caption.trim());
  }

  if (request.altText?.trim()) {
    formData.append('altText', request.altText.trim());
  }

  if (request.role?.trim()) {
    formData.append('role', request.role.trim());
  }

  if (typeof request.sortOrder === 'number') {
    formData.append('sortOrder', String(request.sortOrder));
  }

  const payload = await apiRequest<unknown>(`/api/v1/knowledge/documents/${documentId}/attachments`, {
    method: 'POST',
    body: formData,
  });

  return normalizeKnowledgeAttachment(payload);
};

export const updateKnowledgeDocumentAttachment = async (
  documentId: string,
  attachmentId: string,
  request: UpdateKnowledgeAttachmentRequest,
): Promise<KnowledgeAttachment> => {
  const payload = await apiRequest<unknown>(`/api/v1/knowledge/documents/${documentId}/attachments/${attachmentId}`, {
    method: 'PATCH',
    body: JSON.stringify(request),
  });

  return normalizeKnowledgeAttachment(payload);
};

export const deleteKnowledgeDocumentAttachment = async (
  documentId: string,
  attachmentId: string,
): Promise<void> => {
  await apiRequest<void>(`/api/v1/knowledge/documents/${documentId}/attachments/${attachmentId}`, {
    method: 'DELETE',
  });
};

export const reorderKnowledgeDocumentAttachments = async (
  documentId: string,
  attachmentIds: string[],
): Promise<KnowledgeAttachment[]> => {
  const payload = await apiRequest<unknown>(`/api/v1/knowledge/documents/${documentId}/attachments/reorder`, {
    method: 'PUT',
    body: JSON.stringify({ attachmentIds }),
  });

  return Array.isArray(payload) ? payload.map(normalizeKnowledgeAttachment) : [];
};

export const updateKnowledgeDocumentVisibility = async (
  documentId: string,
  visibility: KnowledgeVisibility,
): Promise<KnowledgeDocument> => {
  const payload = await apiRequest<any>(`/api/v1/knowledge/documents/${documentId}/visibility`, {
    method: 'PATCH',
    body: JSON.stringify({ visibility }),
  });

  return normalizeKnowledgeDocument(payload);
};

export const reprocessKnowledgeDocument = async (
  documentId: string,
  request: ReprocessKnowledgeDocumentRequest = {},
): Promise<KnowledgeIngestionJob> => {
  const payload = await apiRequest<any>(`/api/v1/knowledge/documents/${documentId}/reprocess`, {
    method: 'POST',
    body: JSON.stringify({ force: Boolean(request.force) }),
  });

  return normalizeIngestionJob(payload);
};

export const retrieveKnowledge = async (
  request: KnowledgeRetrieveRequest,
): Promise<KnowledgeRetrieveResponse> => {
  const payload = await apiRequest<any>('/api/v1/knowledge/retrieve', {
    method: 'POST',
    body: JSON.stringify({
      query: request.query,
      maxChunks: request.maxChunks ?? 5,
      audience: request.audience ?? 'employee',
    }),
  });

  return normalizeRetrieveResponse(payload);
};

export const listKnowledgeRetrievalLogs = async (
  query: KnowledgeRetrievalLogListQuery = {},
): Promise<PagedKnowledgeRetrievalLogResult> => {
  const params = new URLSearchParams();

  params.set('page', String(query.page && query.page > 0 ? query.page : 1));
  params.set('pageSize', String(query.pageSize && query.pageSize > 0 ? query.pageSize : 20));

  if (query.conversationId?.trim()) {
    params.set('conversationId', query.conversationId.trim());
  }

  if (query.contextType && query.contextType !== 'all') {
    params.set('contextType', query.contextType);
  }

  const payload = await apiRequest<any>(`/api/v1/knowledge/retrieval-logs?${params.toString()}`);
  return normalizePagedRetrievalLogResult(payload);
};

export const submitKnowledgeRetrievalFeedback = async (
  logId: string,
  request: SubmitKnowledgeRetrievalFeedbackRequest,
): Promise<KnowledgeRetrievalLog> => {
  const payload = await apiRequest<any>(`/api/v1/knowledge/retrieval-logs/${logId}/feedback`, {
    method: 'POST',
    body: JSON.stringify({
      helpful: request.helpful,
      feedback: request.feedback?.trim() || undefined,
    }),
  });

  return normalizeKnowledgeRetrievalLog(payload);
};

export const getKnowledgeBaseSettings = async (): Promise<KnowledgeBaseTenantSettingsResponse> => {
  const payload = await apiRequest<any>('/api/v1/knowledge/settings');
  return normalizeKnowledgeBaseTenantSettingsResponse(payload);
};

export const getKnowledgeBaseHealth = async (): Promise<KnowledgeBaseHealth> => {
  const payload = await apiRequest<any>('/api/v1/knowledge/health');
  return normalizeKnowledgeBaseHealth(payload);
};

export const getKnowledgeBaseMetrics = async (
  lookbackHours = 24,
): Promise<KnowledgeRetrievalMetrics> => {
  const normalizedLookback = Math.max(1, Math.min(lookbackHours, 24 * 30));
  const payload = await apiRequest<any>(`/api/v1/knowledge/metrics?lookbackHours=${normalizedLookback}`);
  return normalizeKnowledgeRetrievalMetrics(payload);
};

export const updateKnowledgeBaseSettings = async (
  request: UpdateKnowledgeBaseSettingsRequest,
): Promise<KnowledgeBaseTenantSettingsResponse> => {
  const payload = await apiRequest<any>('/api/v1/knowledge/settings', {
    method: 'PUT',
    body: JSON.stringify(request),
  });

  return normalizeKnowledgeBaseTenantSettingsResponse(payload);
};

const normalizeKnowledgeGoogleSheetsAuthType = (value: unknown): KnowledgeGoogleSheetsSource['authType'] => {
  const normalized = String(value ?? 'public').toLowerCase();
  if (normalized === 'service_account') {
    return 'service_account';
  }
  if (normalized === 'oauth') {
    return 'oauth';
  }
  return 'public';
};

const normalizeKnowledgeGoogleSheetsSource = (value: any): KnowledgeGoogleSheetsSource | undefined => {
  const spreadsheetId = value?.spreadsheetId ?? value?.SpreadsheetId;
  if (!spreadsheetId) {
    return undefined;
  }

  return {
    spreadsheetId: String(spreadsheetId),
    sheetGid: String(value?.sheetGid ?? value?.SheetGid ?? '0'),
    syncIntervalMinutes: typeof (value?.syncIntervalMinutes ?? value?.SyncIntervalMinutes) === 'number'
      && (value?.syncIntervalMinutes ?? value?.SyncIntervalMinutes) > 0
      ? (value?.syncIntervalMinutes ?? value?.SyncIntervalMinutes)
      : 0,
    lastSyncedAt: typeof (value?.lastSyncedAt ?? value?.LastSyncedAt) === 'string'
      ? (value?.lastSyncedAt ?? value?.LastSyncedAt)
      : undefined,
    authType: normalizeKnowledgeGoogleSheetsAuthType(value?.authType ?? value?.AuthType),
    authConfigured: Boolean(value?.authConfigured ?? value?.AuthConfigured),
  };
};

const normalizeKnowledgeConsultationSource = (value: any): KnowledgeConsultationSource | undefined => {
  const templateId = value?.templateId ?? value?.TemplateId;
  const fieldDefinitionId = value?.fieldDefinitionId ?? value?.FieldDefinitionId;
  if (!templateId || !fieldDefinitionId) {
    return undefined;
  }

  return {
    templateId: String(templateId),
    fieldDefinitionId: String(fieldDefinitionId),
    flattenRepeater: value?.flattenRepeater ?? value?.FlattenRepeater ?? true,
    syncIntervalMinutes: typeof (value?.syncIntervalMinutes ?? value?.SyncIntervalMinutes) === 'number'
      && (value?.syncIntervalMinutes ?? value?.SyncIntervalMinutes) > 0
      ? (value?.syncIntervalMinutes ?? value?.SyncIntervalMinutes)
      : 0,
    lastSyncedAt: typeof (value?.lastSyncedAt ?? value?.LastSyncedAt) === 'string'
      ? (value?.lastSyncedAt ?? value?.LastSyncedAt)
      : undefined,
    includeStatuses: Array.isArray(value?.includeStatuses ?? value?.IncludeStatuses)
      ? (value?.includeStatuses ?? value?.IncludeStatuses).map((item: unknown) => String(item)).filter(Boolean)
      : [],
    includeMetadataFields: normalizeKnowledgeConsultationMetadataFields(
      value?.includeMetadataFields ?? value?.IncludeMetadataFields,
    ),
    additionalFieldDefinitionIds: Array.isArray(value?.additionalFieldDefinitionIds ?? value?.AdditionalFieldDefinitionIds)
      ? (value?.additionalFieldDefinitionIds ?? value?.AdditionalFieldDefinitionIds)
        .map((item: unknown) => String(item))
        .filter(Boolean)
      : [],
    autoSyncOnConsultationChange: value?.autoSyncOnConsultationChange ?? value?.AutoSyncOnConsultationChange ?? true,
    autoSyncOnFieldValueChange: value?.autoSyncOnFieldValueChange ?? value?.AutoSyncOnFieldValueChange ?? true,
  };
};

const normalizeKnowledgeDataSourceType = (value: any): KnowledgeDataSourceType => {
  const normalized = String(value?.dataSourceType ?? value?.DataSourceType ?? 'document').toLowerCase();
  if (normalized === 'consultation') {
    return 'consultation';
  }
  if (normalized === 'csv') {
    return 'csv';
  }
  if (normalized === 'google_sheets') {
    return 'google_sheets';
  }
  return 'document';
};

const normalizeKnowledgeExternalSyncAuthType = (value: any): KnowledgeExternalSyncAuthType => {
  const normalized = String(value?.externalSyncAuthType ?? value?.ExternalSyncAuthType ?? 'none').toLowerCase();
  if (normalized === 'bearer') {
    return 'bearer';
  }
  if (normalized === 'api_key_header') {
    return 'api_key_header';
  }
  return 'none';
};

const normalizeKnowledgeDataCategory = (value: any): KnowledgeDataCategory => ({
  categoryId: String(value?.categoryId ?? ''),
  displayName: String(value?.displayName ?? ''),
  description: typeof value?.description === 'string' ? value.description : '',
  dataRootPath: typeof value?.dataRootPath === 'string' ? value.dataRootPath : '',
  dataSourceType: normalizeKnowledgeDataSourceType(value),
  externalSyncUrl: typeof value?.externalSyncUrl === 'string' ? value.externalSyncUrl : '',
  externalSyncTitle: typeof value?.externalSyncTitle === 'string' ? value.externalSyncTitle : '',
  externalSyncIntervalMinutes: typeof value?.externalSyncIntervalMinutes === 'number'
    && value.externalSyncIntervalMinutes > 0
    ? value.externalSyncIntervalMinutes
    : 0,
  externalSyncLastSyncedAt: typeof value?.externalSyncLastSyncedAt === 'string'
    ? value.externalSyncLastSyncedAt
    : undefined,
  externalSyncAuthType: normalizeKnowledgeExternalSyncAuthType(value),
  externalSyncAuthHeaderName: typeof (value?.externalSyncAuthHeaderName ?? value?.ExternalSyncAuthHeaderName) === 'string'
    ? (value?.externalSyncAuthHeaderName ?? value?.ExternalSyncAuthHeaderName)
    : '',
  externalSyncAuthConfigured: Boolean(value?.externalSyncAuthConfigured ?? value?.ExternalSyncAuthConfigured),
  consultationSource: normalizeKnowledgeConsultationSource(value?.consultationSource ?? value?.ConsultationSource)
    ?? (
      value?.consultationTemplateId || value?.ConsultationTemplateId
        ? {
            templateId: String(value?.consultationTemplateId ?? value?.ConsultationTemplateId ?? ''),
            fieldDefinitionId: String(value?.consultationFieldDefinitionId ?? value?.ConsultationFieldDefinitionId ?? ''),
            flattenRepeater: value?.consultationFlattenRepeater ?? value?.ConsultationFlattenRepeater ?? true,
            syncIntervalMinutes: typeof (value?.consultationSyncIntervalMinutes ?? value?.ConsultationSyncIntervalMinutes) === 'number'
              ? (value?.consultationSyncIntervalMinutes ?? value?.ConsultationSyncIntervalMinutes)
              : 0,
            lastSyncedAt: typeof (value?.consultationLastSyncedAt ?? value?.ConsultationLastSyncedAt) === 'string'
              ? (value?.consultationLastSyncedAt ?? value?.ConsultationLastSyncedAt)
              : undefined,
            includeStatuses: Array.isArray(value?.consultationIncludeStatuses ?? value?.ConsultationIncludeStatuses)
              ? (value?.consultationIncludeStatuses ?? value?.ConsultationIncludeStatuses).map((item: unknown) => String(item)).filter(Boolean)
              : [],
            includeMetadataFields: normalizeKnowledgeConsultationMetadataFields(
              value?.consultationIncludeMetadataFields ?? value?.ConsultationIncludeMetadataFields,
            ),
            additionalFieldDefinitionIds: Array.isArray(
              value?.consultationAdditionalFieldDefinitionIds ?? value?.ConsultationAdditionalFieldDefinitionIds,
            )
              ? (value?.consultationAdditionalFieldDefinitionIds ?? value?.ConsultationAdditionalFieldDefinitionIds)
                .map((item: unknown) => String(item))
                .filter(Boolean)
              : [],
            autoSyncOnConsultationChange:
              value?.consultationAutoSyncOnConsultationChange
              ?? value?.ConsultationAutoSyncOnConsultationChange
              ?? true,
            autoSyncOnFieldValueChange:
              value?.consultationAutoSyncOnFieldValueChange
              ?? value?.ConsultationAutoSyncOnFieldValueChange
              ?? true,
          }
        : undefined
    ),
  googleSheetsSource: normalizeKnowledgeGoogleSheetsSource(value?.googleSheetsSource ?? value?.GoogleSheetsSource)
    ?? (
      value?.googleSheetsSpreadsheetId || value?.GoogleSheetsSpreadsheetId
        ? {
            spreadsheetId: String(value?.googleSheetsSpreadsheetId ?? value?.GoogleSheetsSpreadsheetId ?? ''),
            sheetGid: String(value?.googleSheetsSheetGid ?? value?.GoogleSheetsSheetGid ?? '0'),
            syncIntervalMinutes: typeof (value?.googleSheetsSyncIntervalMinutes ?? value?.GoogleSheetsSyncIntervalMinutes) === 'number'
              ? (value?.googleSheetsSyncIntervalMinutes ?? value?.GoogleSheetsSyncIntervalMinutes)
              : 0,
            lastSyncedAt: typeof (value?.googleSheetsLastSyncedAt ?? value?.GoogleSheetsLastSyncedAt) === 'string'
              ? (value?.googleSheetsLastSyncedAt ?? value?.GoogleSheetsLastSyncedAt)
              : undefined,
            authType: normalizeKnowledgeGoogleSheetsAuthType(
              value?.googleSheetsAuthType ?? value?.GoogleSheetsAuthType,
            ),
            authConfigured: Boolean(value?.googleSheetsAuthConfigured ?? value?.GoogleSheetsAuthConfigured),
          }
        : undefined
    ),
});

const normalizeKnowledgeDataCategoryOverview = (value: any): KnowledgeDataCategoryOverview => ({
  ...normalizeKnowledgeDataCategory(value),
  toolCount: Number(value?.toolCount ?? 0),
  documentCount: Number(value?.documentCount ?? 0),
  readyDocumentCount: Number(value?.readyDocumentCount ?? 0),
  latestDocumentTitle: typeof value?.latestDocumentTitle === 'string' ? value.latestDocumentTitle : undefined,
  latestDocumentUpdatedAt: typeof value?.latestDocumentUpdatedAt === 'string' ? value.latestDocumentUpdatedAt : undefined,
});

const normalizeKnowledgeDynamicToolParameter = (value: any) => ({
  name: String(value?.name ?? ''),
  description: typeof value?.description === 'string' ? value.description : '',
  jsonPath: typeof value?.jsonPath === 'string' ? value.jsonPath : String(value?.name ?? ''),
  matchMode: typeof value?.matchMode === 'string' ? value.matchMode : 'contains',
  isRequired: Boolean(value?.isRequired),
  allowedValues: Array.isArray(value?.allowedValues)
    ? value.allowedValues.map((item: unknown) => String(item)).filter(Boolean)
    : [],
  example: typeof value?.example === 'string' ? value.example : '',
  format: typeof value?.format === 'string' ? value.format : '',
  defaultValue: typeof value?.defaultValue === 'string' ? value.defaultValue : '',
});

const normalizeKnowledgeDynamicToolResponseField = (value: any) => ({
  name: String(value?.name ?? ''),
  jsonPath: typeof value?.jsonPath === 'string' ? value.jsonPath : String(value?.name ?? ''),
});

const normalizeKnowledgeDynamicTool = (value: any): KnowledgeDynamicTool => ({
  toolId: String(value?.toolId ?? ''),
  categoryId: String(value?.categoryId ?? ''),
  kernelFunctionName: String(value?.kernelFunctionName ?? ''),
  displayName: String(value?.displayName ?? ''),
  description: String(value?.description ?? ''),
  dataRootPath: typeof value?.dataRootPath === 'string' ? value.dataRootPath : '',
  functionId: typeof value?.functionId === 'string'
    ? value.functionId
    : `dynamic.${String(value?.toolId ?? '')}`,
  parameters: Array.isArray(value?.parameters)
    ? value.parameters.map(normalizeKnowledgeDynamicToolParameter)
    : [],
  responseFields: Array.isArray(value?.responseFields)
    ? value.responseFields.map(normalizeKnowledgeDynamicToolResponseField)
    : [],
  responseInstruction: typeof value?.responseInstruction === 'string' ? value.responseInstruction : '',
  maxResults: typeof value?.maxResults === 'number' && value.maxResults > 0 ? value.maxResults : 100,
  sortJsonPath: typeof value?.sortJsonPath === 'string' ? value.sortJsonPath : '',
  sortDirection: value?.sortDirection === 'desc' ? 'desc' : 'asc',
  filterLogic: value?.filterLogic === 'or' ? 'or' : 'and',
  resultMode: value?.resultMode === 'count' || value?.resultMode === 'groupby'
    ? value.resultMode
    : 'list',
  groupByJsonPath: typeof value?.groupByJsonPath === 'string' ? value.groupByJsonPath : '',
  isPublished: value?.isPublished !== false,
  knowledgeSearchParameter: typeof value?.knowledgeSearchParameter === 'string'
    ? value.knowledgeSearchParameter
    : '',
  knowledgeSearchMaxChunks: typeof value?.knowledgeSearchMaxChunks === 'number' && value.knowledgeSearchMaxChunks > 0
    ? value.knowledgeSearchMaxChunks
    : 3,
});

const normalizeJsonSchemaIntrospectionResult = (value: any): JsonSchemaIntrospectionResult => ({
  categoryId: String(value?.categoryId ?? ''),
  dataRootPath: typeof value?.dataRootPath === 'string' ? value.dataRootPath : '',
  isSynthetic: value?.isSynthetic === true || value?.IsSynthetic === true,
  fields: Array.isArray(value?.fields)
    ? value.fields.map((field: any) => ({
      jsonPath: String(field?.jsonPath ?? ''),
      name: String(field?.name ?? ''),
      type: String(field?.type ?? 'string'),
      sampleValues: Array.isArray(field?.sampleValues)
        ? field.sampleValues.map((sample: unknown) => String(sample))
        : [],
    }))
    : [],
});

export const listKnowledgeDataCategories = async (): Promise<KnowledgeDataCategory[]> => {
  const payload = await apiRequest<any[]>('/api/v1/knowledge/categories');
  return Array.isArray(payload) ? payload.map(normalizeKnowledgeDataCategory) : [];
};

export const listKnowledgeDataCategoryOverview = async (): Promise<KnowledgeDataCategoryOverview[]> => {
  const payload = await apiRequest<any[]>('/api/v1/knowledge/categories/overview');
  return Array.isArray(payload) ? payload.map(normalizeKnowledgeDataCategoryOverview) : [];
};

export const createKnowledgeDataCategory = async (
  request: CreateKnowledgeDataCategoryRequest,
): Promise<KnowledgeDataCategory> => {
  const payload = await apiRequest<any>('/api/v1/knowledge/categories', {
    method: 'POST',
    body: JSON.stringify(request),
  });

  return normalizeKnowledgeDataCategory(payload);
};

export const updateKnowledgeDataCategory = async (
  categoryId: string,
  request: UpdateKnowledgeDataCategoryRequest,
): Promise<KnowledgeDataCategory> => {
  const payload = await apiRequest<any>(`/api/v1/knowledge/categories/${encodeURIComponent(categoryId)}`, {
    method: 'PUT',
    body: JSON.stringify(request),
  });

  return normalizeKnowledgeDataCategory(payload);
};

export const deleteKnowledgeDataCategory = async (categoryId: string): Promise<void> => {
  await apiRequest<void>(`/api/v1/knowledge/categories/${encodeURIComponent(categoryId)}`, {
    method: 'DELETE',
  });
};

export const syncKnowledgeCategoryExternal = async (
  categoryId: string,
): Promise<KnowledgeCategoryJsonSyncResult> => {
  const payload = await apiRequest<any>(
    `/api/v1/knowledge/categories/${encodeURIComponent(categoryId)}/sync-external`,
    { method: 'POST' },
  );

  return {
    documentId: String(payload?.documentId ?? ''),
    title: typeof payload?.title === 'string' ? payload.title : '',
    updatedAt: typeof payload?.updatedAt === 'string' ? payload.updatedAt : '',
  };
};

const readGoogleSheetsString = (value: any, camelName: string, pascalName: string) => {
  const candidate = value?.[camelName] ?? value?.[pascalName] ?? value?.[`o${pascalName.slice(1)}`];
  return typeof candidate === 'string' ? candidate : '';
};

const readGoogleSheetsBool = (value: any, camelName: string, pascalName: string) =>
  Boolean(value?.[camelName] ?? value?.[pascalName] ?? value?.[`o${pascalName.slice(1)}`]);

const normalizeKnowledgeGoogleSheetsSettingsResponse = (value: any): KnowledgeGoogleSheetsSettingsResponse => ({
  oauthClientId: readGoogleSheetsString(value, 'oauthClientId', 'OAuthClientId'),
  oauthClientSecretConfigured: readGoogleSheetsBool(value, 'oauthClientSecretConfigured', 'OAuthClientSecretConfigured'),
  oauthRedirectUri: readGoogleSheetsString(value, 'oauthRedirectUri', 'OAuthRedirectUri'),
  oauthFrontendReturnUrl: readGoogleSheetsString(value, 'oauthFrontendReturnUrl', 'OAuthFrontendReturnUrl'),
  oauthFrontendReturnUrlResolved: readGoogleSheetsString(
    value,
    'oauthFrontendReturnUrlResolved',
    'OAuthFrontendReturnUrlResolved',
  ),
  oauthAppConfigured: readGoogleSheetsBool(value, 'oauthAppConfigured', 'OAuthAppConfigured'),
  oauthAppManagedByPlatform: readGoogleSheetsBool(value, 'oauthAppManagedByPlatform', 'OAuthAppManagedByPlatform'),
  googleAccountConnected: readGoogleSheetsBool(value, 'googleAccountConnected', 'GoogleAccountConnected'),
});

export const getKnowledgeGoogleSheetsSettings = async (): Promise<KnowledgeGoogleSheetsSettingsResponse> => {
  const payload = await apiRequest<any>('/api/v1/knowledge/google-sheets/settings');
  return normalizeKnowledgeGoogleSheetsSettingsResponse(payload);
};

export const updateKnowledgeGoogleSheetsSettings = async (
  request: UpdateKnowledgeGoogleSheetsSettingsRequest,
): Promise<KnowledgeGoogleSheetsSettingsResponse> => {
  const payload = await apiRequest<any>('/api/v1/knowledge/google-sheets/settings', {
    method: 'PUT',
    body: JSON.stringify({
      oauthClientId: request.oauthClientId,
      oauthClientSecret: request.oauthClientSecret,
      oauthRedirectUri: request.oauthRedirectUri,
      oauthFrontendReturnUrl: request.oauthFrontendReturnUrl,
      clearOAuthClientSecret: request.clearOAuthClientSecret,
    }),
  });
  return normalizeKnowledgeGoogleSheetsSettingsResponse(payload);
};

export const startKnowledgeGoogleSheetsTenantOAuth = async (): Promise<GoogleSheetsOAuthStartResponse> => {
  const payload = await apiRequest<any>('/api/v1/knowledge/google-sheets/oauth/start', {
    method: 'POST',
  });

  return {
    authorizeUrl: String(payload?.authorizeUrl ?? payload?.AuthorizeUrl ?? ''),
  };
};

export const disconnectKnowledgeGoogleSheetsAccount = async (): Promise<KnowledgeGoogleSheetsSettingsResponse> => {
  const payload = await apiRequest<any>('/api/v1/knowledge/google-sheets/oauth/disconnect', {
    method: 'POST',
  });
  return normalizeKnowledgeGoogleSheetsSettingsResponse(payload);
};

export const startKnowledgeGoogleSheetsOAuth = async (
  categoryId: string,
): Promise<GoogleSheetsOAuthStartResponse> => {
  const payload = await apiRequest<any>(
    `/api/v1/knowledge/categories/${encodeURIComponent(categoryId)}/google-sheets/oauth/start`,
    { method: 'POST' },
  );

  return {
    authorizeUrl: String(payload?.authorizeUrl ?? payload?.AuthorizeUrl ?? ''),
  };
};

export const syncKnowledgeCategoryGoogleSheets = async (
  categoryId: string,
): Promise<KnowledgeCategoryJsonSyncResult> => {
  const payload = await apiRequest<any>(
    `/api/v1/knowledge/categories/${encodeURIComponent(categoryId)}/sync-google-sheets`,
    { method: 'POST' },
  );

  return {
    documentId: String(payload?.documentId ?? ''),
    title: typeof payload?.title === 'string' ? payload.title : '',
    updatedAt: typeof payload?.updatedAt === 'string' ? payload.updatedAt : '',
  };
};

export const syncKnowledgeCategoryConsultation = async (
  categoryId: string,
): Promise<KnowledgeCategoryJsonSyncResult> => {
  const payload = await apiRequest<any>(
    `/api/v1/knowledge/categories/${encodeURIComponent(categoryId)}/sync-consultation`,
    { method: 'POST' },
  );

  return {
    documentId: String(payload?.documentId ?? ''),
    title: typeof payload?.title === 'string' ? payload.title : '',
    updatedAt: typeof payload?.updatedAt === 'string' ? payload.updatedAt : '',
  };
};

export const uploadKnowledgeCategoryCsv = async (
  categoryId: string,
  file: File,
  title?: string,
): Promise<KnowledgeCategoryJsonSyncResult> => {
  const formData = new FormData();
  formData.append('file', file);
  if (title?.trim()) {
    formData.append('title', title.trim());
  }

  const payload = await apiRequest<any>(
    `/api/v1/knowledge/categories/${encodeURIComponent(categoryId)}/upload-csv`,
    {
      method: 'POST',
      body: formData,
    },
  );

  return {
    documentId: String(payload?.documentId ?? ''),
    title: typeof payload?.title === 'string' ? payload.title : '',
    updatedAt: typeof payload?.updatedAt === 'string' ? payload.updatedAt : '',
  };
};

export const uploadKnowledgeCategoryJson = async (
  categoryId: string,
  file: File,
  title?: string,
): Promise<KnowledgeCategoryJsonSyncResult> => {
  const formData = new FormData();
  formData.append('file', file);
  if (title?.trim()) {
    formData.append('title', title.trim());
  }

  const payload = await apiRequest<any>(
    `/api/v1/knowledge/categories/${encodeURIComponent(categoryId)}/upload-json`,
    {
      method: 'POST',
      body: formData,
    },
  );

  return {
    documentId: String(payload?.documentId ?? ''),
    title: typeof payload?.title === 'string' ? payload.title : '',
    updatedAt: typeof payload?.updatedAt === 'string' ? payload.updatedAt : '',
  };
};

export const listKnowledgeDynamicTools = async (): Promise<KnowledgeDynamicTool[]> => {
  const payload = await apiRequest<any[]>('/api/v1/knowledge/dynamic-tools');
  return Array.isArray(payload) ? payload.map(normalizeKnowledgeDynamicTool) : [];
};

export const createKnowledgeDynamicTool = async (
  request: CreateKnowledgeDynamicToolRequest,
): Promise<KnowledgeDynamicTool> => {
  const payload = await apiRequest<any>('/api/v1/knowledge/dynamic-tools', {
    method: 'POST',
    body: JSON.stringify(request),
  });

  return normalizeKnowledgeDynamicTool(payload);
};

export const updateKnowledgeDynamicTool = async (
  toolId: string,
  request: UpdateKnowledgeDynamicToolRequest,
): Promise<KnowledgeDynamicTool> => {
  const payload = await apiRequest<any>(`/api/v1/knowledge/dynamic-tools/${encodeURIComponent(toolId)}`, {
    method: 'PUT',
    body: JSON.stringify(request),
  });

  return normalizeKnowledgeDynamicTool(payload);
};

export const deleteKnowledgeDynamicTool = async (toolId: string): Promise<void> => {
  await apiRequest<void>(`/api/v1/knowledge/dynamic-tools/${encodeURIComponent(toolId)}`, {
    method: 'DELETE',
  });
};

export const introspectKnowledgeJsonSchema = async (
  request: IntrospectKnowledgeJsonSchemaRequest,
): Promise<JsonSchemaIntrospectionResult> => {
  const payload = await apiRequest<any>('/api/v1/knowledge/dynamic-tools/introspect-schema', {
    method: 'POST',
    body: JSON.stringify(request),
  });

  return normalizeJsonSchemaIntrospectionResult(payload);
};

export const previewKnowledgeDynamicTool = async (
  request: PreviewKnowledgeDynamicToolRequest,
): Promise<PreviewKnowledgeDynamicToolResult> => {
  const payload = await apiRequest<any>('/api/v1/knowledge/dynamic-tools/preview', {
    method: 'POST',
    body: JSON.stringify(request),
  });

  return payload && typeof payload === 'object' ? payload : {};
};

const normalizeChangeActivityItem = (value: any): ChangeActivityItem => ({
  id: String(value?.id ?? ''),
  area: String(value?.area ?? ''),
  entityId: String(value?.entityId ?? ''),
  entityLabel: String(value?.entityLabel ?? ''),
  action: String(value?.action ?? ''),
  detail: typeof value?.detail === 'string' && value.detail.trim() ? value.detail : null,
  actorEmployeeId: String(value?.actorEmployeeId ?? ''),
  actorName: String(value?.actorName ?? ''),
  isSystem: Boolean(value?.isSystem),
  createdAt: String(value?.createdAt ?? ''),
});

export const listKnowledgeFileActivity = async (
  query: ChangeActivityListQuery = {},
): Promise<PagedChangeActivityResult> => {
  const params = new URLSearchParams();
  params.set('page', String(query.page && query.page > 0 ? query.page : 1));
  params.set('pageSize', String(query.pageSize && query.pageSize > 0 ? query.pageSize : 25));

  if (query.documentId?.trim()) {
    params.set('documentId', query.documentId.trim());
  }

  if (query.action && query.action !== 'all') {
    params.set('action', query.action);
  }

  const payload = await apiRequest<any>(`/api/v1/knowledge/activity?${params.toString()}`);
  const items = Array.isArray(payload?.items) ? payload.items.map(normalizeChangeActivityItem) : [];

  return {
    page: Number(payload?.page ?? 1),
    pageSize: Number(payload?.pageSize ?? 25),
    totalCount: Number(payload?.totalCount ?? items.length),
    items,
  };
};